'use client'

import * as React from 'react'
import { cn } from '../../../../../lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from '../../../Avatar'
import { Icon } from '../../../Icon'
import WorkflowChip from '../../../WorkflowChip'
import type { WorkflowChipProps } from '../../../WorkflowChip'
import type { KanbanTileComponent } from './tileTypes'
import { KanbanItemPreviewContext } from '../../../DataView/Kanban/itemPreviewContext'
import {
  EMPTY_VALUE_LABEL,
  getBooleanAccentStyles,
  getColorAccentStyles,
} from './badgeStyles'

const isModifiedClick = (event: React.MouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

const stopTileDrag = (event: React.SyntheticEvent) => {
  event.stopPropagation()
}

const preventTileDragStart = (event: React.DragEvent) => {
  event.preventDefault()
  event.stopPropagation()
}

const useInView = (enabled: boolean) => {
  const ref = React.useRef<HTMLSpanElement | null>(null)
  const [inView, setInView] = React.useState(!enabled)

  React.useEffect(() => {
    if (!enabled || inView) {
      return
    }
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [enabled, inView])

  return { ref, inView }
}

const formatDate = (
  value: string | Date | null,
  format: 'relative' | 'short' | 'long' = 'short',
): string => {
  if (!value) return EMPTY_VALUE_LABEL
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE_LABEL

  if (format === 'relative') {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days === -1) return 'Tomorrow'
    if (days > 1 && days < 7) return `In ${days} days`
    if (days < -1 && days > -7) return `${Math.abs(days)} days ago`
    if (days >= 7 && days < 30) return `${Math.floor(days / 7)} weeks ago`
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  }

  if (format === 'long') {
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatNumber = (
  value: number | null,
  format: 'number' | 'currency' | 'percent' = 'number',
  prefix?: string,
  suffix?: string,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return EMPTY_VALUE_LABEL
  }

  let formatted: string
  if (format === 'currency') {
    formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value)
  } else if (format === 'percent') {
    const normalized = value <= 1 && value >= -1 ? value * 100 : value
    formatted = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: normalized % 1 === 0 ? 0 : 1,
    }).format(normalized)
    return `${formatted}%`
  } else {
    formatted = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
    }).format(value)
  }

  return `${prefix || ''}${formatted}${suffix || ''}`
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/55 truncate">
    {children}
  </div>
)

const EmptyValue = ({ className }: { className?: string }) => (
  <span
    className={cn('text-xs font-medium text-muted-foreground/60', className)}
  >
    {EMPTY_VALUE_LABEL}
  </span>
)

const FieldShell = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div className={cn('min-w-0 w-full rounded-md bg-muted/25 py-1', className)}>
    {children}
  </div>
)

const AccentBadge = ({
  label,
  color,
  className,
  title,
}: {
  label: string
  color?: string | null
  className?: string
  title?: string
}) => {
  const accent = getColorAccentStyles(color)
  return (
    <span
      className={cn(
        'inline-flex max-w-full truncate rounded-full px-1.5 py-0.5 text-[10px]',
        accent.className,
        className,
      )}
      style={accent.style}
      title={title || label}
    >
      {label}
    </span>
  )
}

const LazyKanbanAvatar = ({
  url,
  seed,
  label,
}: {
  url?: string
  seed?: string
  label: string
}) => {
  const { ref, inView } = useInView(true)

  return (
    <span ref={ref} className="inline-flex h-6 w-6 shrink-0">
      <Avatar className="h-6 w-6 shrink-0">
        {inView && url ? (
          <AvatarImage src={url} />
        ) : inView && seed ? (
          <AvatarImage seed={seed} />
        ) : null}
        <AvatarFallback className="text-[10px]">
          {label.substring(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
    </span>
  )
}

export const KanbanTileHeader = ({
  component,
  index,
  tileId,
}: {
  component: Extract<KanbanTileComponent, { component: 'Label' }>
  index: number
  tileId?: string
}) => {
  const preview = React.useContext(KanbanItemPreviewContext)
  const labelProps = component.props
  const displayLabel =
    labelProps.label || labelProps.fallbackLabel || EMPTY_VALUE_LABEL

  const handleLabelClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isModifiedClick(event)) {
      event.stopPropagation()
      return
    }
    event.preventDefault()
    event.stopPropagation()
    labelProps.onClick?.(event)
    if (tileId) {
      preview?.onLabelClick(tileId, {
        domRect: event.currentTarget.getBoundingClientRect(),
      })
    }
  }

  const handleLabelDoubleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    labelProps.onDoubleClick?.(event)
    if (tileId && preview) {
      preview.onLabelDoubleClick(tileId)
      return
    }
    if (labelProps.href && typeof window !== 'undefined') {
      window.open(labelProps.href, '_self')
    }
  }

  const labelClassName = cn(
    'text-sm font-medium text-foreground/90 truncate flex-1 min-w-0 cursor-pointer',
    labelProps.href &&
      'underline decoration-dotted underline-offset-2 hover:text-foreground hover:no-underline',
  )

  return (
    <div
      key={`label-${index}`}
      className="flex flex-col gap-0.5 w-full pb-0.5 border-b border-border/40"
    >
      <div className="flex items-start justify-between gap-2 w-full">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {labelProps.showAvatar && labelProps.avatar && (
            <LazyKanbanAvatar
              url={labelProps.avatar.url}
              seed={labelProps.avatar.seed}
              label={displayLabel}
            />
          )}
          {labelProps.href ? (
            <a
              href={labelProps.href}
              data-kanban-instance-label
              draggable={false}
              className={labelClassName}
              onClick={handleLabelClick}
              onDoubleClick={handleLabelDoubleClick}
              onMouseDown={stopTileDrag}
              onPointerDown={stopTileDrag}
              onDragStart={preventTileDragStart}
            >
              {displayLabel}
            </a>
          ) : (
            <button
              type="button"
              data-kanban-instance-label
              draggable={false}
              className={cn(labelClassName, 'bg-transparent text-left')}
              onClick={handleLabelClick}
              onDoubleClick={handleLabelDoubleClick}
              onMouseDown={stopTileDrag}
              onPointerDown={stopTileDrag}
              onDragStart={preventTileDragStart}
            >
              {displayLabel}
            </button>
          )}
        </div>
        {labelProps.statusBadge &&
          (labelProps.statusBadge.isBoolean ? (
            (() => {
              const boolAccent = getBooleanAccentStyles(
                labelProps.statusBadge.booleanValue === true,
              )
              return (
                <span
                  className={cn(
                    'shrink-0 inline-flex items-center justify-center w-5 h-5 rounded',
                    boolAccent.className,
                  )}
                  style={boolAccent.style}
                  title={labelProps.statusBadge.booleanValue ? 'Yes' : 'No'}
                >
                  <Icon
                    name={labelProps.statusBadge.booleanValue ? 'Check' : 'X'}
                    size={12}
                  />
                </span>
              )
            })()
          ) : (
            <AccentBadge
              label={labelProps.statusBadge.label}
              color={labelProps.statusBadge.color}
              className="shrink-0 max-w-[120px]"
            />
          ))}
      </div>
      {labelProps.description && (
        <div className="text-xs text-muted-foreground/70 line-clamp-4 leading-relaxed">
          {labelProps.description}
        </div>
      )}
    </div>
  )
}

export const KanbanTileField = ({
  component,
}: {
  component: KanbanTileComponent
}) => {
  if (component.component === 'Date') {
    const dateProps = component.props
    const formatted = formatDate(dateProps.value, dateProps.format)
    const isEmpty = formatted === EMPTY_VALUE_LABEL
    return (
      <FieldShell>
        {dateProps.label && <FieldLabel>{dateProps.label}</FieldLabel>}
        <div
          className={cn(
            'mt-0.5 flex items-center gap-1 text-xs tabular-nums',
            isEmpty
              ? 'text-muted-foreground/60'
              : 'font-medium text-foreground/85',
          )}
        >
          {!isEmpty && (
            <Icon
              name="Calendar"
              size={11}
              className="shrink-0 text-muted-foreground/60"
            />
          )}
          <span className="truncate">{formatted}</span>
        </div>
      </FieldShell>
    )
  }

  if (component.component === 'Number') {
    const numProps = component.props
    const formatted = formatNumber(
      numProps.value,
      numProps.format,
      numProps.prefix,
      numProps.suffix,
    )
    const isEmpty = formatted === EMPTY_VALUE_LABEL
    return (
      <FieldShell>
        {numProps.label && <FieldLabel>{numProps.label}</FieldLabel>}
        <div
          className={cn(
            'mt-0.5 text-xs tabular-nums truncate',
            isEmpty
              ? 'font-medium text-muted-foreground/60'
              : 'font-semibold text-foreground/90',
          )}
        >
          {formatted}
        </div>
      </FieldShell>
    )
  }

  if (component.component === 'Boolean') {
    const boolProps = component.props
    if (boolProps.value === null || boolProps.value === undefined) {
      return (
        <FieldShell>
          {boolProps.label && <FieldLabel>{boolProps.label}</FieldLabel>}
          <div className="mt-0.5">
            <EmptyValue />
          </div>
        </FieldShell>
      )
    }

    const isTrue = boolProps.value === true
    const boolAccent = getBooleanAccentStyles(isTrue)
    return (
      <FieldShell>
        {boolProps.label && <FieldLabel>{boolProps.label}</FieldLabel>}
        <div className="mt-0.5 flex items-center gap-1 text-xs font-medium">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5',
              boolAccent.className,
            )}
            style={boolAccent.style}
          >
            <Icon name={isTrue ? 'Check' : 'X'} size={11} />
            {isTrue ? 'Yes' : 'No'}
          </span>
        </div>
      </FieldShell>
    )
  }

  if (component.component === 'Tags') {
    const tagsProps = component.props
    return (
      <FieldShell>
        {tagsProps.label && <FieldLabel>{tagsProps.label}</FieldLabel>}
        <div className="mt-0.5 flex flex-wrap gap-1">
          {tagsProps.items.length === 0 ? (
            <EmptyValue />
          ) : (
            tagsProps.items.map((tag) => (
              <AccentBadge
                key={`${tagsProps.id}-${tag.id}`}
                label={tag.label}
                color={tag.color}
                className="text-[11px] font-medium"
              />
            ))
          )}
        </div>
      </FieldShell>
    )
  }

  if (component.component === 'Text') {
    const textProps = component.props
    const rawValue = String(textProps.value ?? '').trim()
    const isEmpty = rawValue.length === 0
    const displayValue = isEmpty
      ? EMPTY_VALUE_LABEL
      : textProps.truncate
        ? rawValue.length > textProps.truncate
          ? `${rawValue.substring(0, textProps.truncate)}...`
          : rawValue
        : rawValue

    if (textProps.variant === 'phone') {
      return (
        <FieldShell>
          {textProps.label && <FieldLabel>{textProps.label}</FieldLabel>}
          {isEmpty ? (
            <div className="mt-0.5">
              <EmptyValue />
            </div>
          ) : (
            <a
              href={`tel:${rawValue.replace(/\s+/g, '')}`}
              className="mt-0.5 flex w-full min-w-0 cursor-pointer items-center gap-1.5 text-xs font-medium text-foreground/90 hover:text-foreground"
              onClick={(event) => event.stopPropagation()}
              onMouseDown={stopTileDrag}
              onPointerDown={stopTileDrag}
            >
              <Icon
                name="Phone"
                size={12}
                className="shrink-0 text-muted-foreground/70"
              />
              <span className="truncate">{displayValue}</span>
            </a>
          )}
        </FieldShell>
      )
    }

    if (textProps.variant === 'note') {
      return (
        <FieldShell className="bg-muted/30">
          {textProps.label && <FieldLabel>{textProps.label}</FieldLabel>}
          <p
            className={cn(
              'mt-0.5 text-xs leading-relaxed line-clamp-3',
              isEmpty
                ? 'font-medium text-muted-foreground/60'
                : 'text-muted-foreground/85',
            )}
          >
            {displayValue}
          </p>
        </FieldShell>
      )
    }

    return (
      <FieldShell>
        {textProps.label && <FieldLabel>{textProps.label}</FieldLabel>}
        <div
          className={cn(
            'mt-0.5 text-xs',
            isEmpty
              ? 'font-medium text-muted-foreground/60'
              : 'font-medium text-foreground/85',
            !isEmpty && 'line-clamp-2',
          )}
        >
          {displayValue}
        </div>
      </FieldShell>
    )
  }

  if (component.component === 'Progress') {
    const progressProps = component.props
    const hasValue =
      progressProps.value !== null &&
      progressProps.value !== undefined &&
      !Number.isNaN(progressProps.value)
    const max = progressProps.max || 100
    const percent = hasValue
      ? Math.min(100, Math.max(0, (progressProps.value! / max) * 100))
      : 0
    return (
      <FieldShell>
        {(progressProps.label || progressProps.showPercent) && (
          <div className="flex items-center justify-between gap-2 mb-1">
            {progressProps.label && (
              <FieldLabel>{progressProps.label}</FieldLabel>
            )}
            {progressProps.showPercent && hasValue && (
              <span className="text-[10px] font-medium tabular-nums text-muted-foreground/70">
                {Math.round(percent)}%
              </span>
            )}
          </div>
        )}
        {hasValue ? (
          <div className="h-1.5 w-full rounded-full bg-muted/60 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary/75 transition-all duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
        ) : (
          <EmptyValue />
        )}
      </FieldShell>
    )
  }

  if (component.component === 'Image') {
    const imgProps = component.props
    const sizeClass = {
      sm: 'h-8 w-8',
      md: 'h-10 w-10',
      lg: 'h-12 w-12',
    }[imgProps.size || 'md']

    return (
      <FieldShell>
        {imgProps.alt && <FieldLabel>{imgProps.alt}</FieldLabel>}
        {imgProps.src ? (
          <img
            src={imgProps.src}
            alt={imgProps.alt || ''}
            className={cn(
              'mt-0.5 rounded-md object-cover ring-1 ring-border/40',
              sizeClass,
            )}
          />
        ) : (
          <EmptyValue className="mt-0.5" />
        )}
      </FieldShell>
    )
  }

  return null
}

export const KanbanFooterField = ({
  component,
  index,
  tileId,
}: {
  component: KanbanTileComponent
  index: number
  tileId: string
}) => {
  if (component.component === 'WorkflowChips') {
    const workflowChipsProps = component.props
    const chips: WorkflowChipProps[] = Array.isArray(workflowChipsProps.chips)
      ? workflowChipsProps.chips
      : []
    return (
      <div key={`footer-workflows-${index}`} className="flex flex-wrap gap-1">
        {chips.length === 0 ? (
          <EmptyValue />
        ) : (
          chips.map((chip, chipIndex) => (
            <div
              key={`${tileId}-wfchip-${chipIndex}`}
              className="cursor-pointer"
              onClick={(event) => {
                event.stopPropagation()
                chip.onClick?.()
              }}
              onMouseDown={stopTileDrag}
              onPointerDown={stopTileDrag}
            >
              <WorkflowChip {...chip} />
            </div>
          ))
        )}
      </div>
    )
  }

  return null
}
